import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWithAuth } from "../../../auth/api";
import {
  HiClipboardList,
  HiPencilAlt,
  HiPhotograph,
  HiUpload,
  HiArrowLeft,
  HiPlusCircle,
  HiArrowRight,
  HiInformationCircle,
  HiSelector,
} from "react-icons/hi";

import { compressImage, dataURLtoFile } from "../../../utils/imageCompressor";

function CreateGuideStep() {
  const navigate = useNavigate();
  const [guideTypes, setGuideTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    guideTypes_id: "",
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    const fetchGuideTypes = async () => {
      try {
        const response = await fetchWithAuth("get", "guideTypes");
        setGuideTypes(response.data || []);
      } catch (error) {
        toast.error("Failed to load guide categories");
      }
    };
    fetchGuideTypes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    if (selectedFiles.length + files.length > 5) {
      toast.warning("You can attach up to 5 images.");
    }

    try {
      const remainingSlots = 5 - selectedFiles.length;
      const filesToProcess = files.slice(0, remainingSlots);

      const processedFiles = await Promise.all(
        filesToProcess.map(async (file) => {
          const dataUrl = await compressImage(file, 300 * 1024);
          if (dataUrl) {
            return dataURLtoFile(dataUrl, file.name);
          }
          return file;
        })
      );

      const validFiles = processedFiles.filter(Boolean);
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      setPreviews((prev) => [
        ...prev,
        ...validFiles.map((file) => URL.createObjectURL(file)),
      ]);
    } catch (err) {
      console.error("Compression error:", err);
    }
  };

  const removeImage = (index) => {
    setSelectedFiles((prev) => prev.filter((_, idx) => idx !== index));
    setPreviews((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name.trim() ||
      !formData.description.trim() ||
      !formData.guideTypes_id ||
      selectedFiles.length === 0
    ) {
      toast.warning("All fields are mandatory. Please upload at least one image.");
      return;
    }

    setIsLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("guideTypes_id", formData.guideTypes_id);

      selectedFiles.forEach((file) => {
        formDataToSend.append("images", file);
      });

      const response = await fetchWithAuth(
        "POST",
        "createGuideSteps",
        formDataToSend,
      );

      if (response.status === 201 || response.ok) {
        toast.success("Workflow step created successfully!");
        navigate("/admin/AllGuideSteps");
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to create step");
      }
    } catch (error) {
      toast.error("Network error. Operation aborted.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/admin/AllGuideSteps"
          className="p-2 hover:bg-white rounded-xl text-slate-400 hover:text-blue-600 transition-all shadow-sm"
        >
          <HiArrowLeft size={20} />
        </Link>
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
            Add Step to Workflow
          </h2>
          <p className="text-slate-500 text-sm font-medium">
            Define a new technical or logistical step for your system guides.
          </p>
        </div>
      </div>

      <div className="glass rounded-[2rem] p-8 md:p-12 shadow-xl border border-slate-200/60 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full -mr-32 -mt-32 transition-transform duration-700 group-hover:scale-110"></div>

        <form onSubmit={handleSubmit} className="space-y-10 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Left Column */}
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 flex items-center gap-2 ml-1">
                  <HiPlusCircle className="text-blue-500" /> Step Title
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="e.g. Initial Configuration"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-field"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 flex items-center gap-2 ml-1">
                  <HiSelector className="text-blue-500" /> Associated Type
                </label>
                <select
                  name="guideTypes_id"
                  value={formData.guideTypes_id}
                  onChange={handleChange}
                  className="input-field appearance-none cursor-pointer"
                  required
                >
                  <option value="">Select Target Category</option>
                  {guideTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 flex items-center gap-2 ml-1">
                  <HiInformationCircle className="text-blue-500" /> Instructions
                </label>
                <textarea
                  name="description"
                  placeholder="Provide clear, actionable instructions for this step..."
                  rows="4"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className="input-field py-3 resize-none"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <div className="flex justify-between items-center ml-1">
                <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <HiPhotograph className="text-blue-500" /> Guide Step Images (Multi-upload)
                </label>
                <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                  Auto-compressed &lt; 300KB
                </span>
              </div>

              <div className="space-y-4">
                <label className="flex flex-col items-center justify-center w-full min-h-[160px] border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50 hover:bg-white hover:border-blue-400 transition-all cursor-pointer group/inner shadow-inner p-6">
                  <div className="w-12 h-12 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center mb-2 group-hover/inner:scale-110 group-hover/inner:text-blue-600 transition-all text-slate-400">
                    <HiUpload size={24} />
                  </div>
                  <span className="text-xs font-bold text-slate-600 uppercase tracking-widest text-center">
                    Click to attach step graphics/photos
                  </span>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">
                    Upload up to 5 images • Auto-compressed
                  </p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>

                {previews.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {previews.map((src, idx) => (
                      <div
                        key={idx}
                        className="relative aspect-square rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-100 group/item"
                      >
                        <img src={src} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full text-xs shadow-md opacity-90 hover:opacity-100 transition-opacity"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="pt-6">
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-4 rounded-2xl flex items-center justify-center gap-3 shadow-xl shadow-blue-500/20 group relative overflow-hidden active:scale-[0.98] transition-all disabled:opacity-70"
            >
              {isLoading ? (
                <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <span className="font-bold tracking-wide text-lg">
                    Integrate Step
                  </span>
                  <HiClipboardList
                    className="group-hover:scale-110 transition-transform duration-300"
                    size={24}
                  />
                  <HiArrowRight
                    className="absolute right-8 opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all"
                    size={20}
                  />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateGuideStep;
